/**
 * Export utilities for CSV and simple report generation
 */

export function exportToCSV(data: Record<string, any>[], filename: string) {
  if (data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(","),
    ...data.map((row) =>
      headers
        .map((h) => {
          const val = row[h];
          if (val === null || val === undefined) return "";
          const str = typeof val === "object" ? JSON.stringify(val) : String(val);
          // Escape quotes and wrap in quotes if contains comma/newline/quote
          if (str.includes(",") || str.includes("\n") || str.includes('"')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        })
        .join(",")
    ),
  ];

  const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  downloadBlob(blob, `${filename}.csv`);
}

export function exportServerReport(servers: any[]) {
  const data = servers.map((s) => ({
    Hostname: s.hostname,
    IP: s.ip_address,
    OS: s.os,
    Status: s.online ? "Online" : "Offline",
    Tags: (s.tags || []).join("; "),
    CPU: s.cpu_usage?.toFixed(1) ?? "N/A",
    Memory: s.memory_usage?.toFixed(1) ?? "N/A",
    Disk: s.disk_usage?.toFixed(1) ?? "N/A",
    Uptime_Hours: s.uptime_seconds ? Math.round(s.uptime_seconds / 3600) : "N/A",
    Last_Seen: s.last_seen ? new Date(s.last_seen).toISOString() : "Never",
    Network_In_Mbps: s.network_in_mbps?.toFixed(2) ?? "0",
    Network_Out_Mbps: s.network_out_mbps?.toFixed(2) ?? "0",
    Network_Errors: s.network_errors ?? 0,
    Firewall_Enabled: s.firewall_enabled ?? "N/A",
    OS_Edition: s.os_edition ?? "N/A",
  }));

  const now = new Date().toISOString().slice(0, 10);
  exportToCSV(data, `server-report-${now}`);
}

export function exportMetricsHistory(metrics: any[], hostname: string) {
  const data = metrics.map((m) => ({
    Timestamp: m.timestamp,
    CPU: m.cpu_usage?.toFixed(1) ?? "",
    Memory: m.memory_usage?.toFixed(1) ?? "",
    Disk: m.disk_usage?.toFixed(1) ?? "",
    Network_In: m.network_in_mbps?.toFixed(2) ?? "",
    Network_Out: m.network_out_mbps?.toFixed(2) ?? "",
    Disk_IOPS: m.disk_iops ?? "",
    Process_Count: m.process_count ?? "",
    Load_Average: m.load_average ?? "",
  }));

  const now = new Date().toISOString().slice(0, 10);
  exportToCSV(data, `metrics-${hostname}-${now}`);
}

export function exportAuditLogs(logs: any[]) {
  const data = logs.map((l) => ({
    Timestamp: l.timestamp,
    Action: l.action_type,
    Resource: l.resource_type,
    Resource_ID: l.resource_id ?? "",
    Success: l.success ? "Yes" : "No",
    Risk_Level: l.risk_level,
    IP: l.ip_address ?? "",
    User_Agent: l.user_agent ?? "",
  }));

  const now = new Date().toISOString().slice(0, 10);
  exportToCSV(data, `audit-logs-${now}`);
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
